import React, { Fragment, useEffect, useRef, useState } from "react";
import { ArrowUp } from "lucide-react";
import "./style.css";
import Navbar from "../../../components/Landing_page_Navbar/index";
import priceImage from "../../../assets/images/price-img.webp";
import { Link } from "react-router-dom";

const PLANS = [
  {
    name: "Free",
    eyebrow: "Explore the network",
    description: "Get discovered and test the essentials before you scale.",
    monthly: 0,
    annual: 0,
    color: "slate",
    cta: "Start for free",
    featured: false,
    features: ["100 public connections", "20 catalog items", "1 storefront", "Community support"],
  },
  {
    name: "Basic",
    eyebrow: "For growing suppliers",
    description: "Build a repeatable sourcing motion with the tools to stay visible.",
    monthly: 4999,
    annual: 3999,
    color: "blue",
    cta: "Choose Basic",
    featured: false,
    marketing: {
      title: "Marketing Agent: Promote",
      summary: "Your agent promotes your shop and items to a defined buyer audience while you run the business.",
      items: [
        "Up to 5 promotional ads per month",
        "Promote shop + selected items to up to 250 clients",
        "Agent posts and manages campaigns",
        "Monthly market research snapshot",
      ],
    },
    features: ["1,000 public connections", "100 catalog items", "2 storefronts", "Verified supplier badge"],
  },
  {
    name: "Standard",
    eyebrow: "For active procurement teams",
    description: "The best balance of reach, control, and insight for serious growth.",
    monthly: 14999,
    annual: 11999,
    color: "royal",
    cta: "Choose Standard",
    featured: true,
    marketing: {
      title: "Marketing Agent: Optimize",
      summary: "Go beyond posting. Your agent researches performance, replies to opportunities, and recommends what to do next.",
      items: [
        "Up to 12 promotional ads per month",
        "Promote to up to 750 relevant clients",
        "Agent replies to campaign inquiries",
        "Performance reports + recommendations",
        "Ongoing market and competitor research",
      ],
    },
    features: ["5,000 public connections", "300 catalog items", "5 storefronts", "Priority matching & analytics"],
  },
  {
    name: "Premium",
    eyebrow: "For enterprise networks",
    description: "Unlock higher limits and a managed path for complex procurement.",
    monthly: null,
    annual: null,
    color: "navy",
    cta: "Talk to an expert",
    featured: false,
    marketing: {
      title: "Marketing Agent: Managed growth",
      summary: "A hands-on marketing partner for your shop, catalog, promotions, and buyer conversations.",
      items: [
        "Up to 25 promotional ads per month",
        "Promote to up to 2,000 relevant clients",
        "Agent replies, follows up, and manages reports",
        "Applies item sales promotions",
        "Shares promotions across relevant channels",
        "Strategic recommendations and campaign planning",
      ],
    },
    features: ["Unlimited public connections", "Unlimited catalog items", "20 storefronts", "Dedicated account support"],
  },
];

// Har row: [label, Free, Basic, Standard, Premium]
const COMPARISON_GROUPS = [
  {
    label: "Network & connections",
    rows: [
      ["General public connections", "100", "1,000", "5,000", "Unlimited"],
      ["Wholesaler network connections", "10", "100", "500", "5,000"],
      ["Industrial plant connections", "10", "100", "500", "5,000"],
      ["Raw material supplier connections", "10", "100", "500", "5,000"],
      ["General / ancillary connections", "20", "200", "1,000", "10,000"],
    ],
  },
  {
    label: "Inventory & classified listings",
    rows: [
      ["Simultaneous live ads", "5 ads", "20 ads", "40 ads", "60 ads"],
      ["Catalog items", "20 items", "100 items", "300 items", "Unlimited"],

    ],
  },
  {
    label: "Storefronts & visibility",
    rows: [
      ["Independent storefronts", "1 store", "2 stores", "5 stores", "20 stores"],

    ],
  },
  {
    label: "Support & insights",
    rows: [
      ["Buyer intent insights", "—", "Monthly", "Weekly", "Real-time"],
      ["Account support", "Community", "Email", "Priority", "Dedicated"],
    
    ],
  },
  {
    label: "Marketing Agent",
    rows: [
      ["Promotional ads posted per month", "1", "5", "12", "25"],
      ["Promotional reach", "Starter guidance", "Up to 250 clients", "Up to 750 clients", "Up to 2,000 clients"],
      ["Campaign posting & management", "Checklist", "Included", "Included", "Managed"],
      ["Market research & optimization", "Basic tips", "Monthly snapshot", "Ongoing research", "Strategic planning"],
      ["Inquiry replies & reports", "—", "—", "Replies + reports", "Replies, follow-ups + reports"],
      ["Sales promotions & sharing", "—", "—", "Recommendations", "Applied and shared by agent"],
    ],
  },
];

const FAQS = [
  {
    question: "Can I start with Free and upgrade later?",
    answer:
      "Yes. Start with the Free plan to build your supplier profile and explore the network. You can upgrade whenever your connection volume or team needs change, and your new limits apply immediately.",
  },
  {
    question: "Is there a discount for annual billing?",
    answer:
      "Annual billing is priced at the equivalent of two months free on Basic and Standard. Premium plans are scoped around your network and procurement requirements, so the team will include commercial terms in your proposal.",
  },
  {
    question: "What counts as a connection?",
    answer:
      "A connection is a live relationship between your business and another verified participant in the Selectezi network. The connection limits help keep outreach relevant and give every plan a clear path to scale.",
  },
  {
    question: "Do paid plans include onboarding?",
    answer:
      "Basic and Standard include guided setup resources. Standard includes priority support during onboarding, while Premium includes a managed launch plan, workspace configuration, and a dedicated account contact.",
  },
  {
    question: "What does the Marketing Agent do for my shop?",
    answer:
      "The Marketing Agent posts and manages promotional ads for your shop and selected items according to your package limit. Basic focuses on promotion and a monthly market snapshot; Standard adds campaign replies, performance reports, and ongoing research; Premium adds follow-ups, sales promotions, promotional sharing, and strategic recommendations managed for you.",
  },
  {
    question: "Are taxes included in the displayed price?",
    answer:
      "Prices are shown in Pakistani Rupees (PKR) before applicable provincial sales tax. Any relevant PRA, SRB, or KPRA taxes are itemized at checkout or in your Premium proposal.",
  },
];

const HIGHLIGHTS = [
  { icon: "users", value: "4 plan paths", label: "for every stage of growth" },
  { icon: "chart", value: "Clear limits", label: "so teams can plan ahead" },
  { icon: "shield", value: "PKR billing", label: "with transparent tax notes" },
];

const STEPS = [
  {
    number: "01",
    icon: "building",
    title: "Create your presence",
    copy: "Build a trusted supplier profile, add your catalog, and set the categories that matter to your business.",
  },
  {
    number: "02",
    icon: "users",
    title: "Connect with intent",
    copy: "Find the right buyer, supplier, or industrial partner with connection limits designed to stay relevant.",
  },
  {
    number: "03",
    icon: "chart",
    title: "Grow with visibility",
    copy: "Unlock more storefronts, richer insights, and team access as your operation gains momentum.",
  },
];

const HERO_METRICS = [
  ["Connections", "2,840"],
  ["Live listings", "248"],
  ["Matched buyers", "96"],
];

const CHART_BARS = [34, 48, 42, 64, 57, 76, 82, 96, 88, 100, 108, 120];

/* -------------------------------- Helpers -------------------------------- */

function formatPrice(value) {
  if (value === null || value === undefined) return null;
  return new Intl.NumberFormat("en-PK").format(value);
}

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
}

/* ---------------------------------- Icons -------------------------------- */
// Chhote inline SVG icons (koi extra icon library install nahi karni)

const ICON_PATHS = {
  arrowRight: (
    <>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </>
  ),
  chart: (
    <>
      <path d="M3 3v18h18" />
      <path d="M18 17V9" />
      <path d="M13 17V5" />
      <path d="M8 17v-3" />
    </>
  ),
  building: (
    <>
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
      <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
      <path d="M10 6h4" />
      <path d="M10 10h4" />
      <path d="M10 14h4" />
      <path d="M10 18h4" />
    </>
  ),
  check: <path d="M20 6 9 17l-5-5" />,
  chevronDown: <path d="m6 9 6 6 6-6" />,
  help: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </>
  ),
  menu: (
    <>
      <line x1="4" x2="20" y1="12" y2="12" />
      <line x1="4" x2="20" y1="6" y2="6" />
      <line x1="4" x2="20" y1="18" y2="18" />
    </>
  ),
  package: (
    <>
      <path d="m16 16 2 2 4-4" />
      <path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14" />
      <path d="m7.5 4.27 9 5.15" />
      <polyline points="3.29 7 12 12 20.71 7" />
      <line x1="12" x2="12" y1="22" y2="12" />
    </>
  ),
  shield: (
    <>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  sparkles: (
    <>
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
      <path d="M4 17v2" />
      <path d="M5 18H3" />
    </>
  ),
  users: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  close: (
    <>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </>
  ),
  zap: (
    <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
  ),
};

function Icon({ name, size = 16, className = "" }) {
  return (
    <svg
      className={`sx-icon ${className}`.trim()}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}


function useToast() {
  const [toast, setToast] = useState(null);
  const timer = useRef(null);

  const notify = (title, description) => {
    clearTimeout(timer.current);
    setToast({ title, description });
    timer.current = setTimeout(() => setToast(null), 4000);
  };

  const close = () => {
    clearTimeout(timer.current);
    setToast(null);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  return { toast, notify, close };
}

function Toaster({ toast, onClose }) {
  return (
    <div className="toast-container position-fixed bottom-0 end-0 p-3 sx-toaster">
      {toast && (
        <div className="toast show sx-toast" role="status" aria-live="polite">
          <div className="d-flex align-items-start gap-3 p-3">
            <span className="sx-toast__icon">
              <Icon name="check" size={14} />
            </span>
            <div className="flex-grow-1">
              <div className="sx-toast__title">{toast.title}</div>
              {toast.description && <div className="sx-toast__text">{toast.description}</div>}
            </div>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </div>
        </div>
      )}
    </div>
  );
}

/* --------------------------------- Sections ------------------------------ */

  

function Hero() {
  return (
    <section className="hero-section pt-0" style={{background: `white`}}>
      <div className="hero-orb hero-orb--one" />
      <div className="hero-orb hero-orb--two" />

      <div className="site-container hero-inner position-relative">
        <div className="row hero-row align-items-stretch gy-5 gx-lg-5">
          <div className="col-lg-6">
            <div className="hero-copy">
             
              <h2 className="display-title text-dark">
                More Visibility For Your Shop <br></br> <span> More Opportunities For Your Business</span>
              </h2>
              <p className="lead-copy mt-4 ">
            Choose a package with the marketing support you need to promote your products, reach the right clients, and keep growing.
              </p>

              <div className="hero-actions d-flex flex-column flex-sm-row gap-3">
                <a className="btn sx-btn sx-btn--primary sx-btn--lg" href="#plans">
                  Explore plans <Icon name="arrowRight" />
                </a>
                <a className="btn sx-btn sx-btn--secondary sx-btn--lg" href="#comparison">
                  See what&apos;s included
                </a>
              </div>

              <div className="hero-checks d-flex flex-wrap align-items-center">
                {["No setup fee", "Upgrade anytime", "PKR billing"].map((text) => (
                  <span key={text} className="hero-checks__item">
                    <Icon name="check" className="sx-icon--blue" /> {text}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="col-lg-6 hero-preview-col">
            <div className="hero-preview hero-preview--image">
              <img
                className="hero-preview__image"
                src={priceImage}
                alt="Selectezi pricing dashboard preview"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Highlights() {
  return (
    <section id="why-selectezi" className="highlights">
      <div className="site-container">
        <div className="row g-0">
          {HIGHLIGHTS.map((item) => (
            <div key={item.value} className="col-12 col-sm-6 col-lg-4 highlight-col">
              <div className="highlight-item d-flex align-items-center gap-3">
                <span className="feature-icon">
                  <Icon name={item.icon} size={20} />
                </span>
                <div>
                  <div className="highlight-item__value">{item.value}</div>
                  <div className="highlight-item__label">{item.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PlanCard({ plan, annual, onSelect }) {
  const price = annual ? plan.annual : plan.monthly;

  let billingNote = "Billed monthly";
  if (price === null) billingNote = "Tailored to your operation";
  else if (price === 0) billingNote = "No card required";
  else if (annual) billingNote = `Billed annually · ${formatPrice(price * 12)} PKR / year`;

  let buttonVariant = "sx-btn--outline";
  if (plan.featured) buttonVariant = "sx-btn--white";
  else if (plan.color === "navy") buttonVariant = "sx-btn--outline-light";

  return (
    <article className={`plan-card plan-card--${plan.color}${plan.featured ? " plan-card--featured" : ""}`}>
      {plan.featured && (
        <div className="plan-ribbon">
          <Icon name="sparkles" size={14} /> Most chosen
        </div>
      )}

      <div className="d-flex align-items-start justify-content-between gap-3">
        <div>
          <div className="plan-eyebrow">{plan.eyebrow}</div>
          <h3 className="plan-name">{plan.name}</h3>
        </div>
        {plan.featured ? (
          <span className="plan-star">
            <Icon name="sparkles" />
          </span>
        ) : (
          <span className="plan-dot" />
        )}
      </div>

      <p className="plan-desc">{plan.description}</p>

      <div className="plan-price">
        {price === null ? (
          <span className="plan-price__value plan-price__value--custom">Custom</span>
        ) : (
          <>
            <span className="plan-price__value">{formatPrice(price)}</span>
            <span className="plan-price__unit">PKR / month</span>
          </>
        )}
      </div>
      <div className="plan-billing">{billingNote}</div>

      <button type="button" className={`btn sx-btn ${buttonVariant} w-100 plan-cta`} onClick={() => onSelect(plan.name)}>
        {plan.cta} <Icon name="arrowRight" />
      </button>

      <div className="plan-includes">
        <div className="plan-includes__label">Includes</div>
        <ul className="check-list-price">
          {plan.features.map((feature) => (
            <li key={feature} className="check-list__item">
              <Icon name="check" className="check-list__icon" />
              {feature}
            </li>
          ))}
        </ul>
      </div>

      {plan.marketing && (
        <div className="marketing-agent-card">
          <div className="marketing-agent-card__head">
            <div>
              <div className="marketing-agent-card__eyebrow">
                <Icon name="sparkles" size={14} /> Marketing Agent
              </div>
              <div className="marketing-agent-card__title">{plan.marketing.title}</div>
            </div>
            <span className="marketing-agent-card__badge">Included</span>
          </div>
          <p className="marketing-agent-card__summary">{plan.marketing.summary}</p>
          <ul className="check-list-price check-list--small">
            {plan.marketing.items.map((item) => (
              <li key={item} className="check-list__item">
                <Icon name="check" size={14} className="check-list__icon" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
}

function Plans({ onSelect }) {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="plans" className="section-pad">
      <div className="site-container">
        <div className="d-flex flex-column flex-lg-row align-items-lg-end justify-content-lg-between gap-4">
          <div className="section-head">
            <div className="eyebrow eyebrow--blue">Choose your operating level</div>
            <h2 className="section-title mt-3">A plan for where you are now <br></br>and where you&apos;re going</h2>
            <p className="section-copy mt-3">
              Start lean, find your rhythm, then unlock the reach and control your next stage demands.
            </p>
          </div>

          {/* <div className="billing-control" role="group" aria-label="Billing frequency">
            <button
              type="button"
              className={`billing-control__btn${!annual ? " is-active" : ""}`}
              onClick={() => setAnnual(false)}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`billing-control__btn${annual ? " is-active" : ""}`}
              onClick={() => setAnnual(true)}
            >
              Annual <span>Save 20%</span>
            </button>
          </div> */}
        </div>

        <div className="row g-3 mt-4">
          {PLANS.map((plan) => (
            <div key={plan.name} className="col-12 col-md-6 col-xl-3 d-flex ">
              <PlanCard plan={plan} annual={annual} onSelect={onSelect} />
            </div>
          ))}
        </div>

        <p className="plans-note">
          All paid plans include core profile verification, encrypted account access, and self serve billing controls.
        </p>
      </div>
    </section>
  );
}

function Comparison() {
  return (
    <section id="comparison" className="section-pad section-pad--tight comparison-section">
      <div className="site-container">
        <div className="section-head">
          <div className="eyebrow eyebrow--blue">Precision metric grid</div>
          <h2 className="section-title mt-3">See the difference at a glance</h2>
          <p className="section-copy mt-3">
            A transparent view of limits, visibility, and support so your team can make a confident choice without a
            sales call.
          </p>
        </div>

        <div className="comparison-shell mt-5">
          <div className="comparison-scroll">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Core capability</th>
                  {PLANS.map((plan) => (
                    <th key={plan.name} className={plan.featured ? "is-highlighted" : ""}>
                      {plan.name}
                      {plan.featured && <span className="table-badge">Popular</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_GROUPS.map((group) => (
                  <Fragment key={group.label}>
                    <tr className="group-row">
                      <td colSpan={5}>{group.label}</td>
                    </tr>
                    {group.rows.map((row) => (
                      <tr key={row[0]}>
                        <td>{row[0]}</td>
                        {row.slice(1).map((value, index) => (
                          <td key={`${row[0]}-${index}`} className={index === 2 ? "is-highlighted" : ""}>
                            {value === "—" ? <span className="cell-empty">—</span> : value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="comparison-foot d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between gap-2">
          <span className="comparison-foot__help">
            <Icon name="help" className="sx-icon--blue" /> Need help choosing? Standard fits most active teams.
          </span>
          <span>Pricing shown in PKR Applicable sales tax added at checkout</span>
        </div>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section className="section-pad">
      <div className="site-container">
        <div className="row gy-5 gx-lg-5 align-items-start">
          <div className="col-lg-5">
            <div className="section-head section-head--narrow">
              <div className="eyebrow eyebrow--blue">Find your growth partner</div>
              <h2 className=" mt-3">Discover the right Marketing Agent for your shop</h2>
              <p className="section-copy mt-3">
           From your first promotional post to ongoing campaign research, Selectezi matches your package with the right level of hands-on support to help your shop get seen, sell more, and grow with confidence.
              </p>
              <a href="#contact" className="btn sx-btn sx-btn--secondary mt-4">
                Talk through your needs <Icon name="arrowRight" />
              </a>
            </div>
          </div>

          <div className="col-lg-7">
            <div className="process-list">
              {STEPS.map((step) => (
                <div className="process-item" key={step.number}>
                  <div className="process-number">{step.number}</div>
                  <div className="process-icon">
                    <Icon name={step.icon} size={20} />
                  </div>
                  <div>
                    <h3 className="process-title">{step.title}</h3>
                    <p className="process-copy">{step.copy}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}




function Footer() {
  return (
     <footer className="selectezi-footer p-3">
  <div className=" d-flex align-items-center justify-content-between flex-wrap gap-2">
    <span>
      &copy; 2025 SELECTEZI Inc. All rights reserved.
    </span>

    <div className="d-flex align-items-center gap-3">
      <Link to="/general-terms">General Terms</Link>
      <Link to="/privacy-policy">Privacy Policy</Link>
    </div>
  </div>
</footer>
  );
}

/* ---------------------------------- Page --------------------------------- */

export default function PricingPage() {
  const { toast, notify, close } = useToast();
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const updateBackToTopVisibility = () => {
      setShowBackToTop(window.scrollY > 320);
    };

    updateBackToTopVisibility();
    window.addEventListener("scroll", updateBackToTopVisibility, {
      passive: true,
    });
    return () =>
      window.removeEventListener("scroll", updateBackToTopVisibility);
  }, []);

  const handlePlanClick = (planName) => {
    if (planName === "Premium") {
      scrollToId("contact");
      return;
    }
    notify(`${planName} selected`, "Your plan is ready to configure. Registration will be connected here");
  };

  return (
    <div className="sx-page">
<Navbar/>
      <main id="top">
        <Hero />
        <Highlights />
        <Plans onSelect={handlePlanClick} />
        <Comparison />
        <Process />

     
      </main>

      <Footer />
      <Toaster toast={toast} onClose={close} />
      <button
        type="button"
        className={`sx-pricing-back-to-top${showBackToTop ? " sx-pricing-back-to-top--visible" : ""}`}
        aria-label="Back to top"
        title="Back to top"
        tabIndex={showBackToTop ? 0 : -1}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <ArrowUp size={21} strokeWidth={2.2} aria-hidden="true" />
      </button>
    </div>
  );
}
